import React, { useCallback, useRef, useState } from "react"
import { Check, Clock, FileText, type LucideIcon } from "lucide-react"
import styles from "./MaterialsQuotationWelcome.module.scss"

export const MATERIALS_QUOTATION_FILE_INPUT_ID = "materials-quotation-file-input"

const ACCEPT =
  "application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

export type MaterialsWelcomeHighlight = {
  icon: LucideIcon
  label: string
}

type MaterialsQuotationWelcomeBlockProps = {
  isLoading: boolean
  onBeginManual: () => void
  onFileChosen: (file: File) => void
  title?: string
  description?: string
  highlights?: MaterialsWelcomeHighlight[]
  manualTitle?: string
  manualDescription?: string
  manualBadge?: string
  manualSteps?: string[]
  manualCta?: string
  manualEstimatedTime?: string
  assistedTitle?: string
  assistedDescription?: string
  assistedBadge?: string
  assistedRecommendedLabel?: string
  assistedBullets?: string[]
  assistedCta?: string
  assistedHint?: string
  assistedFileTypesHint?: string
  assistedEstimatedTime?: string
  assistedDisabled?: boolean
  onAssistedUnavailableClick?: () => void
  onGoToCostEstimation?: () => void
  recommendedFirst?: boolean
}

const MaterialsQuotationWelcomeBlock: React.FC<MaterialsQuotationWelcomeBlockProps> = ({
  isLoading,
  onBeginManual,
  onFileChosen,
  title = "Armá tu listado de materiales",
  description = "Elegí cómo arrancar: manual o asistido con IA. Después publicamos tu lista para recibir presupuestos en Edify y notificamos a proveedores relevantes según la zona indicada.",
  highlights = [],
  manualTitle = "Carga manual",
  manualDescription = "Buscá materiales por nombre o explorá por rubro y armá tu lista desde cero.",
  manualBadge = "Manual",
  manualSteps = [
    "Buscás por nombre o explorás por rubro.",
    "Agregás materiales y ajustás cantidades.",
    "Revisás tu lista antes de publicar.",
    "Publicás y recibís presupuestos de proveedores.",
  ],
  manualCta = "Empezar manualmente",
  manualEstimatedTime = "10–20 minutos",
  assistedTitle = "Desde un archivo",
  assistedDescription = "Subí tu Excel, PDF o Word y la IA detecta materiales y cantidades automáticamente.",
  assistedBadge = "IA",
  assistedRecommendedLabel = "Recomendado",
  assistedBullets = [
    "Detectamos materiales y cantidades del archivo.",
    "Revisás y ajustás la lista antes de publicar.",
    "Publicás y recibís presupuestos de proveedores.",
  ],
  assistedCta = "Elegir archivo",
  assistedHint = "Soltá tu archivo acá o",
  assistedFileTypesHint = "Excel, PDF, Word, CSV · hasta 10 MB",
  assistedEstimatedTime = "2 minutos",
  assistedDisabled = false,
  onAssistedUnavailableClick,
  onGoToCostEstimation,
  recommendedFirst = true,
}) => {
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const selectFile = useCallback(
    (f: File | null | undefined) => {
      if (!f) return
      onFileChosen(f)
    },
    [onFileChosen]
  )

  const assistedCard = (
    <article
      className={`${styles.welcomeChoiceCard} ${styles.welcomeChoiceCardRecommended}`}
      aria-labelledby="materials-welcome-assisted-title"
    >
      <div className={styles.welcomeChoiceCardHeader}>
        <span className={styles.materialsIaBadge}>
          {assistedBadge} · {assistedRecommendedLabel}
        </span>
      </div>
      <h3 id="materials-welcome-assisted-title" className={styles.welcomeChoiceCardTitle}>
        {assistedTitle}
      </h3>
      <p className={styles.welcomeChoiceCardDescription}>{assistedDescription}</p>
      <div
        className={`${styles.welcomeAgentDrop} ${dragOver ? styles.welcomeAgentDropActive : ""} ${assistedDisabled ? styles.welcomeAgentDropDisabled : ""}`}
        onDragOver={(e) => {
          if (assistedDisabled) return
          e.preventDefault()
          e.stopPropagation()
          setDragOver(true)
        }}
        onDragLeave={(e) => {
          if (assistedDisabled) return
          e.preventDefault()
          e.stopPropagation()
          setDragOver(false)
        }}
        onDrop={(e) => {
          if (assistedDisabled) return
          e.preventDefault()
          e.stopPropagation()
          setDragOver(false)
          const f = e.dataTransfer.files?.[0]
          if (f) selectFile(f)
        }}
      >
        <input
          ref={fileRef}
          id={MATERIALS_QUOTATION_FILE_INPUT_ID}
          type="file"
          accept={ACCEPT}
          className={styles.welcomeAssistedFileInput}
          tabIndex={-1}
          onChange={(ev) => {
            const f = ev.target.files?.[0] ?? null
            ev.target.value = ""
            selectFile(f ?? undefined)
          }}
        />
        <FileText size={28} strokeWidth={1.75} className={styles.welcomeAgentDropIcon} aria-hidden />
        <span className={styles.welcomeAgentDropHint}>{assistedHint}</span>
        <button
          type="button"
          className={styles.welcomeButtonOutline}
          onClick={() => {
            if (assistedDisabled) {
              onAssistedUnavailableClick?.()
              return
            }
            fileRef.current?.click()
          }}
          disabled={isLoading}
        >
          {assistedCta}
        </button>
        {assistedFileTypesHint ? (
          <span className={styles.welcomeAgentDropTypes}>{assistedFileTypesHint}</span>
        ) : null}
      </div>
      <ul className={styles.welcomeCheckList}>
        {assistedBullets.map((item, idx) => (
          <li key={`assisted-${idx}`}>
            <Check size={16} strokeWidth={2.5} className={styles.welcomeCheckListIcon} aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {assistedEstimatedTime ? (
        <p className={styles.welcomeEstimatedTime}>
          <Clock size={15} strokeWidth={2} aria-hidden />
          <span>Tiempo estimado: {assistedEstimatedTime}</span>
        </p>
      ) : null}
    </article>
  )

  const manualCard = (
    <article
      className={`${styles.welcomeChoiceCard} ${styles.welcomeChoiceCardManual}`}
      aria-labelledby="materials-welcome-manual-title"
    >
      <div className={styles.welcomeChoiceCardHeader}>
        <span className={styles.manualBadge}>{manualBadge}</span>
      </div>
      <h3 id="materials-welcome-manual-title" className={styles.welcomeChoiceCardTitle}>
        {manualTitle}
      </h3>
      <p className={styles.welcomeChoiceCardDescription}>{manualDescription}</p>
      <ol className={styles.welcomeNumberedSteps}>
        {manualSteps.map((step, index) => (
          <li key={step} className={styles.welcomeNumberedStep}>
            <span className={styles.welcomeNumberedStepIndex} aria-hidden>
              {index + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        className={styles.welcomeButtonOutline}
        onClick={onBeginManual}
        disabled={isLoading}
      >
        {manualCta}
      </button>
      {manualEstimatedTime ? (
        <p className={styles.welcomeEstimatedTime}>
          <Clock size={15} strokeWidth={2} aria-hidden />
          <span>Tiempo estimado: {manualEstimatedTime}</span>
        </p>
      ) : null}
    </article>
  )

  return (
    <div className={styles.welcomeBlock}>
      <div className={styles.welcomeLayout}>
        <div className={styles.welcomeContent}>
          <div className={styles.materialsWelcomeIntro}>
            <h2 className={styles.welcomeTitle}>{title}</h2>
            <p className={styles.welcomeText}>{description}</p>
          </div>
          {highlights.length > 0 ? (
            <ul className={styles.welcomeHighlights}>
              {highlights.map(({ icon: HighlightIcon, label }) => (
                <li key={label} className={styles.welcomeHighlightItem}>
                  <HighlightIcon
                    size={18}
                    strokeWidth={2}
                    className={styles.welcomeHighlightIcon}
                    aria-hidden
                  />
                  <span className={styles.welcomeHighlightLabel}>{label}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className={styles.welcomeCards}>
            {recommendedFirst ? (
              <>
                {assistedCard}
                {manualCard}
              </>
            ) : (
              <>
                {manualCard}
                {assistedCard}
              </>
            )}
          </div>
          {onGoToCostEstimation ? (
            <button
              type="button"
              className={styles.welcomeSecondaryLink}
              onClick={onGoToCostEstimation}
              disabled={isLoading}
            >
              ¿Querés una referencia rápida? Ir a estimación de costos
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default MaterialsQuotationWelcomeBlock
